import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ProductService } from '../../../_services/product.service';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { NgClass, DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-view-modal',
    imports: [NgClass, MatProgressBarModule, MatDialogContent, FormsModule, DecimalPipe, MatIconModule, MatTooltipModule],
    templateUrl: './view-modal.component.html',
    styleUrl: './view-modal.component.css'
})
export class ViewModalComponent implements OnInit {

  readonly dialogRef = inject(MatDialogRef<ViewModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  progress: boolean = false;
  product: any;
  slabsCrates: any;
  frontEndURL = environment.frontEndURL;

  editingStockVariantId: number | null = null;
  stockAdjustment: number = 0;
  stockUpdateProgress: boolean = false;
  canEditStock: boolean = false;

  constructor(
    private productService: ProductService,
    private alertService: AlertService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.canEditStock = this.userService.isSuperAdmin();

    if (this.data) {
      this.progress = true;
      this.getSelectedProduct(this.data.productSlug);
    }
  }

  // ---- what the header tiles and variant cards show ----
  get variants(): any[] {
    return (this.product?.variants ?? []).filter((v: any) => v.trackQty);
  }

  get totalStock(): number {
    return this.variants.reduce((sum, v) => sum + (Number(v.stockQty) || 0), 0);
  }

  effectivePrice(v: any): number {
    return Number(v?.discPrice) > 0 && Number(v.discPrice) < Number(v.price) ? Number(v.discPrice) : Number(v?.price) || 0;
  }

  get fromPrice(): number {
    const prices = this.variants.map((v) => this.effectivePrice(v)).filter((n) => n > 0);
    return prices.length ? Math.min(...prices) : 0;
  }

  savingPercent(v: any): number {
    const price = Number(v?.price) || 0;
    return price > 0 && this.effectivePrice(v) < price ? Math.round(((price - this.effectivePrice(v)) / price) * 100) : 0;
  }

  get bestSaving(): number {
    return this.variants.reduce((best, v) => Math.max(best, this.savingPercent(v)), 0);
  }

  stockState(qty: number): { label: string; cls: string } {
    if (qty <= 0) return { label: 'Out of stock', cls: 'bg-red-50 text-red-700' };
    if (qty <= 5) return { label: 'Low stock', cls: 'bg-orange-50 text-orange-700' };
    return { label: 'In stock', cls: 'bg-emerald-50 text-emerald-700' };
  }

  // A yard's share of the variant's stock, for the little bar.
  yardShare(v: any, yard: 'm' | 'k'): number {
    const total = (Number(v.mStockQty) || 0) + (Number(v.kStockQty) || 0);
    const own = Number(yard === 'm' ? v.mStockQty : v.kStockQty) || 0;
    return total > 0 ? Math.round((own / total) * 100) : 0;
  }

  get categoryPath(): { parent: string | null; name: string | null } {
    const c = this.product?.product?.productcategories?.[0]?.category;
    return { parent: c?.categoryParentIds?.categoryName ?? null, name: c?.categoryName ?? null };
  }

  async copyText(text: string | null | undefined, label: string) {
    try {
      await navigator.clipboard.writeText(String(text ?? ''));
      this.alertService.alert(`${label} copied`, 'success');
    } catch {
      this.alertService.alert('Could not copy - your browser blocked it', 'error');
    }
  }

  private async getSelectedProduct(productSlug: string) {
    await this.productService.getProductBySlug(productSlug).subscribe({
      next: async (response) => {
        this.progress = false;
        this.product = response;
        if (this.product) {
          if (this.product.product.productTemplateId === 2) this.setSlabsCrates(this.product.variants);
        }
      },
      error: (error) => {
        this.alertService.alert(error.error.message, 'error');
      },
    });
  }

  private setSlabsCrates(variants: any) {
    this.slabsCrates = {
      slab: variants.find((variant: any) => variant.sku.includes('SLAB')),
      crate: variants.find((variant: any) => variant.sku.includes('CRATE')),
    };
  }

  openStockEdit(variantId: number) {
    this.editingStockVariantId = variantId;
    this.stockAdjustment = 0;
  }

  cancelStockEdit() {
    this.editingStockVariantId = null;
    this.stockAdjustment = 0;
  }

  saveStockUpdate(variant: any) {
    if (this.stockAdjustment === 0) return;
    this.stockUpdateProgress = true;
    this.productService.updateProductStock(variant.productVariantId, 'Markstey', this.stockAdjustment).subscribe({
      next: () => {
        variant.mStockQty += this.stockAdjustment;
        variant.stockQty += this.stockAdjustment;
        this.alertService.alert('Stock updated successfully', 'success');
        this.editingStockVariantId = null;
        this.stockAdjustment = 0;
        this.stockUpdateProgress = false;
      },
      error: (error) => {
        this.alertService.alert(error?.error?.message ?? 'Stock update failed', 'error');
        this.stockUpdateProgress = false;
      },
    });
  }

}
